import React, { useState, useEffect } from 'react';
import { Card, CategoryBar, Flex, Metric, Text } from '@tremor/react';
import { getDeterministicSpeakerColors } from '../../utils/speakerColors';

function TimeBasedParticipationCard({ duration, fragment, t, getSpeakerColorRaw, getLegendData, speakerNames, ...rest }) {
    const [legendData, setLegendData] = useState([]);

    useEffect(() => {
        // Assuming getLegendData already returns an array of { label, color } objects
        // and needs to be updated to replace speaker identifiers with names.
        if (fragment?.result && speakerNames && getLegendData) {
            try {
                const legendDataResult = getLegendData(fragment.result);
                if (Array.isArray(legendDataResult)) {
                    const updatedLegendData = legendDataResult.map(item => {
                        const speakerName = speakerNames[item.label] ? speakerNames[item.label] : item.label;
                        return { ...item, label: speakerName };
                    });
                    setLegendData(updatedLegendData);
                }
            } catch (error) {
                console.error('Error processing legend data:', error);
                setLegendData([]);
            }
        }
    }, [fragment?.result, speakerNames, getLegendData]);

    return (
        <Card className='flex flex-col h-full justify-between my-1' {...rest}>
            {fragment?.result ? (
                (() => {
                    try {
                        const parsedResult = JSON.parse(fragment.result);
                        if (!parsedResult || typeof parsedResult !== 'object') {
                            return null;
                        }
                        
                        const entries = Object.entries(parsedResult);
                        if (!entries.length) {
                            return null;
                        }

                        return (
                            <>
                                <div>
                                    <Flex alignItems="start">
                                        <p className='font-semibold text-gray-700'>{t('ExpandedMeedingCard.SpeakerOverTimeLabel')}</p>
                                    </Flex>
                                </div>
                                <div>
                                    <CategoryBar className="mt-4"
                                        values={entries.map(([key, value]) => {
                                            return value / duration;
                                        })}
                                        colors={entries.map(([key]) => {
                                            return getSpeakerColorRaw ? getSpeakerColorRaw(key) : 'gray-500';
                                        })}
                                        showLabels={false}
                                    />
                                </div>
                                <div className='w-full flex justify-center items-center'>
                                    {(() => {
                                        console.error('🚨🚨🚨 TIME BASED PARTICIPATION CARD LEGEND RENDERING 🚨🚨🚨');
                                        console.error('📊 Legend data:', legendData);
                                        
                                        if (!Array.isArray(legendData) || legendData.length === 0) {
                                            console.error('❌ TIME BASED PARTICIPATION CARD - Invalid or empty legend data');
                                            return <div className="text-red-500 italic">No participation legend data available</div>;
                                        }
                                        
                                        const categories = legendData.map(item => {
                                            console.error('🏷️ TIME BASED PARTICIPATION CARD Category:', item.label);
                                            return item.label;
                                        });
                                        
                                        // Use deterministic colors based on speaker names for consistency with CategoryBar
                                        const speakerNames = legendData.map(item => item.label);
                                        const colors = getDeterministicSpeakerColors(speakerNames);
                                        
                                        console.error('🎨 TIME BASED PARTICIPATION CARD Deterministic colors:', colors);
                                        
                                        console.error('✅ TIME BASED PARTICIPATION CARD - Rendering Legend');
                                        
                                        // Use custom legend instead of Tremor Legend to prevent Chart.js errors
                                        console.error('✅ TIME PARTICIPATION CARD - Using CUSTOM LEGEND to bypass Tremor/Chart.js issues');
                                        
                                        return (
                                            <div className="mt-3 max-h-32 overflow-y-auto">
                                                <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 px-2">
                                                    {legendData.map((item, index) => (
                                                        <div key={index} className="flex items-center gap-2 flex-shrink-0">
                                                            <div 
                                                                className={`w-3 h-3 rounded bg-${colors[index] || 'gray-500'}`}
                                                            ></div>
                                                            <span className="text-sm text-gray-700">
                                                                {item.label}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </>
                        );
                    } catch (error) {
                        console.error('Error rendering participation data:', error);
                        return (
                            <div className='min-w-full h-32 flex items-center justify-center bg-red-50 text-red-600 text-sm'>
                                Unable to load participation data
                            </div>
                        );
                    }
                })()
            ) : (
                <div className='min-w-full h-32 bg-gray-200 animate-pulse'></div>
            )}
        </Card>
    );
}

export default TimeBasedParticipationCard;
